const SESSION_KEY = "smt_sid";
const FLUSH_INTERVAL = 5000;
const CLICK_BATCH_SIZE = 50;

let sessionId = sessionStorage.getItem(SESSION_KEY);
if (!sessionId) {
  sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  sessionStorage.setItem(SESSION_KEY, sessionId);
}

const eventQueue: any[] = [];
const clickQueue: any[] = [];
let currentPage = "";
let maxScroll = 0;

function getPagePath(): string {
  return window.location.pathname;
}

function truncate(s: string | null, max: number): string {
  if (!s) return "";
  return s.length > max ? s.slice(0, max) : s;
}

function getElementInfo(el: HTMLElement): { tag: string; id: string | null; text: string } {
  return {
    tag: el.tagName.toLowerCase(),
    id: el.id || el.getAttribute("data-track") || null,
    text: truncate(el.textContent?.trim() || el.getAttribute("aria-label") || "", 100),
  };
}

function flush() {
  if (eventQueue.length === 0 && clickQueue.length === 0) return;

  const payload: any = {};
  if (eventQueue.length > 0) {
    payload.events = eventQueue.splice(0);
  }
  if (clickQueue.length > 0) {
    payload.clicks = clickQueue.splice(0, CLICK_BATCH_SIZE);
  }

  try {
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    navigator.sendBeacon("/api/track", blob);
  } catch {
    fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  }
}

function trackEvent(eventType: string, extra?: Record<string, any>) {
  const info = extra?.element ? getElementInfo(extra.element) : { tag: null, id: null, text: null };
  eventQueue.push({
    session_id: sessionId,
    event_type: eventType,
    page_path: getPagePath(),
    element_tag: info.tag,
    element_id: info.id,
    element_text: info.text,
    metadata: extra?.metadata || null,
  });
}

function trackClick(e: MouseEvent) {
  const target = e.target as HTMLElement;
  if (!target) return;

  const info = getElementInfo(target);
  const scrollY = window.scrollY;
  const docH = Math.max(document.documentElement.scrollHeight, 1);
  const vpW = window.innerWidth;
  const vpH = window.innerHeight;

  // Heatmap coordinates (percentage of page)
  const xPct = Math.round((e.clientX / vpW) * 1000) / 10;
  const yPct = Math.round(((e.clientY + scrollY) / docH) * 10000) / 100;

  clickQueue.push({
    page_path: getPagePath(),
    x_pct: xPct,
    y_pct: yPct,
    viewport_w: vpW,
    viewport_h: vpH,
    element_tag: info.tag,
    element_id: info.id,
    element_text: info.text,
  });

  // Also track as event for the activity log
  trackEvent("click", { element: target });
}

function trackScroll() {
  const scrollY = window.scrollY + window.innerHeight;
  const docH = document.documentElement.scrollHeight;
  const pct = Math.round((scrollY / docH) * 100);
  if (pct > maxScroll) maxScroll = pct;
}

function trackPageEnter() {
  const path = getPagePath();
  if (path === currentPage) return;

  // Leave previous page
  if (currentPage) {
    trackPageLeave();
  }

  currentPage = path;
  maxScroll = 0;

  trackEvent("page_view");

  // Send page enter
  try {
    fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page_enter: { session_id: sessionId, page_path: path, referrer: document.referrer || null },
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

function trackPageLeave() {
  if (!currentPage) return;

  try {
    const blob = new Blob([JSON.stringify({
      page_leave: { session_id: sessionId, page_path: currentPage, scroll_depth: maxScroll },
    })], { type: "application/json" });
    navigator.sendBeacon("/api/track", blob);
  } catch {}
}

export function initTracker() {
  // Click tracking
  document.addEventListener("click", trackClick, { passive: true, capture: true });

  // Scroll tracking
  document.addEventListener("scroll", trackScroll, { passive: true });

  // Page visibility (track leave on tab switch)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      trackPageLeave();
      flush();
    }
  });

  // Flush before unload
  window.addEventListener("beforeunload", () => {
    trackPageLeave();
    flush();
  });

  // Initial page view
  trackPageEnter();

  // Periodic flush
  setInterval(flush, FLUSH_INTERVAL);

  // Watch for SPA navigation (React Router)
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...args) {
    origPush.apply(this, args);
    setTimeout(trackPageEnter, 0);
  };
  history.replaceState = function (...args) {
    origReplace.apply(this, args);
    setTimeout(trackPageEnter, 0);
  };
  window.addEventListener("popstate", () => setTimeout(trackPageEnter, 0));
}
